import logger from '../../utils/logger';
import React from 'react';
import { X, Crown, Calendar, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { calculateTrialDaysRemaining } from '../../services/subscriptionService';

const TrialBanner = ({ user, onDismiss }) => {
  const navigate = useNavigate();

  if (!user || !user.isTrialUser) {
    return null;
  }

  const daysRemaining = calculateTrialDaysRemaining(user.planExpiresAt);

  // Don't show if trial has expired
  if (daysRemaining <= 0) {
    return null;
  }

  const isUrgent = daysRemaining <= 2;
  const isWarning = daysRemaining <= 5;

  const getBannerStyle = () => {
    if (isUrgent) {
      return {
        bg: 'bg-gradient-to-r from-red-500 to-pink-500',
        text: 'text-white',
        icon: 'text-white',
        button: 'bg-white text-red-600 hover:bg-gray-100',
      };
    }
    if (isWarning) {
      return {
        bg: 'bg-gradient-to-r from-yellow-500 to-orange-500',
        text: 'text-white',
        icon: 'text-white',
        button: 'bg-white text-orange-600 hover:bg-gray-100',
      };
    }
    return {
      bg: 'bg-gradient-to-r from-purple-500 to-pink-500',
      text: 'text-white',
      icon: 'text-white',
      button: 'bg-white text-purple-600 hover:bg-gray-100',
    };
  };

  const style = getBannerStyle();

  const getMessage = () => {
    if (isUrgent) {
      return `⚠️ Your PRO trial ends in ${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'}!`;
    }
    if (isWarning) {
      return `⏰ Your PRO trial ends in ${daysRemaining} days`;
    }
    return `🎉 You have ${daysRemaining} days left in your PRO trial`;
  };

  return (
    <div className={`${style.bg} ${style.text} shadow-lg sticky top-0 z-50 animate-slide-down`}>
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Icon + Message */}
          <div className="flex items-center gap-3 flex-1">
            <Crown className={`${style.icon} w-6 h-6 flex-shrink-0`} />
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
              <span className="font-semibold text-sm sm:text-base">
                {getMessage()}
              </span>
              <span className="text-xs sm:text-sm opacity-90">
                Upgrade now to keep all PRO features after your trial ends
              </span>
            </div>
          </div>

          {/* Right: CTA + Close */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/subscription')}
              className={`${style.button} flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap`}
            >
              Upgrade Now
              <ArrowRight className="w-4 h-4" />
            </button>
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors duration-200"
                aria-label="Dismiss banner"
              >
                <X className={`${style.icon} w-5 h-5`} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;
